/**
 * What "fit to the walk" will actually produce, worked out in the browser.
 *
 * The server picks the scale for a fitted job (PrintMaps::SheetPlanner), so
 * the panel used to be able to say nothing more useful than "Scale to fit" —
 * the user only learned the scale of their map once the PDF existed. Scale is
 * the most load-bearing fact on a printed sheet, so that is the one thing the
 * page should not be coy about.
 *
 * These functions mirror `PrintMaps::RouteFilter` and
 * `PrintMaps::SheetPlanner#denominator_for` exactly: same main-route filter,
 * same ladder, same 6% padding. They are an estimate only in the sense that
 * the server reads the route from the database while the page reads the
 * feature collection it was handed — the geometry behind both is the same.
 */
import type { Bounds } from './printMapGeometry';
import {
  SCALE_LADDER,
  fitDenominator,
  formatScale,
  halfExtentsMetres,
  mercatorX,
  mercatorY,
} from './printMapGeometry';

/**
 * Blank kept between the walk and the neatline, as a fraction of the sheet —
 * mirrors `PrintMaps::SheetPlanner::SHEET_MARGIN_FRACTION`. The planner lays
 * its strip out against this smaller window, so it is the window a day has to
 * fit inside to come out on a single sheet.
 */
export const SHEET_MARGIN_FRACTION = 0.05;

/**
 * Route styles that frame a sheet, mirroring
 * `PrintMaps::RouteFilter::MAIN_ROUTE_STYLES`.
 *
 * Alternates, spurs and transport links are printed but must not drive the
 * framing: a bus ride to the trailhead would zoom the sheet out to fit a
 * county.
 */
export const MAIN_ROUTE_STYLES: Array<string | null | undefined> = ['route', 'recording', '', null, undefined];

type AnyFeature = GeoJSON.Feature | { geometry?: unknown; properties?: Record<string, unknown> | null };

export function isMainRoute(feature: AnyFeature): boolean {
  const style = (feature?.properties as Record<string, unknown> | null | undefined)?.routeStyle;
  return MAIN_ROUTE_STYLES.includes(style as string | null | undefined);
}

/**
 * The walking route, falling back to everything when a collection carries no
 * main-route features at all — otherwise a hike made entirely of alternates
 * would frame on nothing.
 */
export function mainFeatures(collection: GeoJSON.FeatureCollection | null | undefined): AnyFeature[] {
  const features = (collection?.features ?? []) as AnyFeature[];
  const main = features.filter(isMainRoute);
  return main.length > 0 ? main : features;
}

/** Every [lng, lat] in a GeoJSON geometry, at any nesting depth. */
function positions(geometry: unknown, out: number[][] = []): number[][] {
  const coordinates = (geometry as { coordinates?: unknown } | null)?.coordinates;
  collect(coordinates, out);
  return out;
}

function collect(node: unknown, out: number[][]): void {
  if (!Array.isArray(node)) return;
  if (typeof node[0] === 'number' && typeof node[1] === 'number') {
    out.push(node as number[]);
    return;
  }
  node.forEach((child) => collect(child, out));
}

export function featuresBbox(features: AnyFeature[]): Bounds | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  features.forEach((feature) => {
    positions(feature?.geometry).forEach(([lng, lat]) => {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    });
  });

  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
  return { west, south, east, north };
}

/** Bounding box of the walking route only. */
export function framingBbox(collection: GeoJSON.FeatureCollection | null | undefined): Bounds | null {
  return featuresBbox(mainFeatures(collection));
}

/**
 * One framing box per day, keyed by day number.
 *
 * A day with no main-route features of its own is skipped rather than framed
 * on its alternates — it will not produce a sheet either.
 */
export function bboxesByDay(collection: GeoJSON.FeatureCollection | null | undefined): Map<number, Bounds> {
  const byDay = new Map<number, AnyFeature[]>();

  ((collection?.features ?? []) as AnyFeature[]).forEach((feature) => {
    const day = (feature?.properties as Record<string, unknown> | null | undefined)?.hikeDayNumber;
    if (typeof day !== 'number') return;
    if (!isMainRoute(feature)) return;

    const bucket = byDay.get(day);
    if (bucket) bucket.push(feature);
    else byDay.set(day, [feature]);
  });

  const out = new Map<number, Bounds>();
  Array.from(byDay.keys()).sort((a, b) => a - b).forEach((day) => {
    const bbox = featuresBbox(byDay.get(day) as AnyFeature[]);
    if (bbox) out.set(day, bbox);
  });

  return out;
}

/**
 * The scale the server will choose for this box.
 *
 * Never null: a stretch too long for even the coarsest rung is tiled across
 * several sheets *at* that rung, which is what `SheetPlanner#denominator_for`
 * does with its `|| Layout.scale_ladder.last`.
 */
export function fittedDenominator(
  bbox: Bounds,
  frameWidthMm: number,
  frameHeightMm: number,
  ladder: number[] = SCALE_LADDER,
): number {
  return fitDenominator(bbox, frameWidthMm, frameHeightMm, 0.06, ladder) ?? ladder[ladder.length - 1];
}

/**
 * Every scale a fitted job will print at: one entry for a whole-route job,
 * one per day for a per-day fan-out.
 */
export function fittedDenominators(
  collection: GeoJSON.FeatureCollection | null | undefined,
  frameWidthMm: number,
  frameHeightMm: number,
  perDay: boolean,
  ladder: number[] = SCALE_LADDER,
): number[] {
  if (perDay) {
    const byDay = bboxesByDay(collection);
    if (byDay.size > 0) {
      return Array.from(byDay.values()).map((bbox) => fittedDenominator(bbox, frameWidthMm, frameHeightMm, ladder));
    }
  }

  const bbox = framingBbox(collection);
  return bbox ? [fittedDenominator(bbox, frameWidthMm, frameHeightMm, ladder)] : [];
}

/** "1:50,000", or "1:25,000 – 1:75,000" when the days differ. */
export function describeFittedScale(denominators: number[]): string {
  if (denominators.length === 0) return '';

  const low = Math.min(...denominators);
  const high = Math.max(...denominators);
  return low === high ? formatScale(low) : `${formatScale(low)} – ${formatScale(high)}`;
}

/** True when a per-day job would print its pages at more than one scale. */
export function scalesDiffer(denominators: number[]): boolean {
  return new Set(denominators).size > 1;
}

/**
 * Whether a stretch of walk lands on one sheet at this scale.
 *
 * This reproduces the first step of the planner's strip mapping: it lays the
 * route out against a window inset by SHEET_MARGIN_FRACTION, and a day that
 * fits inside that window is taken whole in one chunk. It deliberately does
 * NOT try to predict how many sheets a day that *doesn't* fit will take —
 * that depends on where the route doubles back, and a wrong count is worse
 * than an honest "more than one".
 */
export function fitsOneSheet(
  bbox: Bounds,
  frameWidthMm: number,
  frameHeightMm: number,
  denominator: number,
): boolean {
  const centerLat = (bbox.north + bbox.south) / 2;
  const [halfW, halfH] = halfExtentsMetres(centerLat, denominator, frameWidthMm, frameHeightMm);
  const inset = 1 - 2 * SHEET_MARGIN_FRACTION;

  return (
    Math.abs(mercatorX(bbox.east) - mercatorX(bbox.west)) <= halfW * 2 * inset &&
    Math.abs(mercatorY(bbox.north) - mercatorY(bbox.south)) <= halfH * 2 * inset
  );
}

/**
 * How many days of a fan-out will run to more than one sheet.
 *
 * `null` denominator means the job is fitting each day, in which case the
 * answer is almost always zero — fitting picks a scale that swallows the day
 * with room to spare. A fixed scale is where "one sheet per day" quietly
 * stops being true.
 */
export function daysOverOneSheet(
  collection: GeoJSON.FeatureCollection | null | undefined,
  frameWidthMm: number,
  frameHeightMm: number,
  denominator: number | null,
  ladder: number[] = SCALE_LADDER,
): { over: number; total: number } {
  const byDay = bboxesByDay(collection);
  const boxes = byDay.size > 0 ? Array.from(byDay.values()) : ([framingBbox(collection)].filter(Boolean) as Bounds[]);

  const over = boxes.filter((bbox) => {
    const scale = denominator ?? fittedDenominator(bbox, frameWidthMm, frameHeightMm, ladder);
    return !fitsOneSheet(bbox, frameWidthMm, frameHeightMm, scale);
  }).length;

  return { over, total: boxes.length };
}
